import { IconActionButton } from '@/components/patterns/admin-page'
import {
  DetailCard,
  DetailIconBadge,
  detailIconBadgeToneClasses,
  detailPaleIconClass,
  getDetailWellClass,
  type DetailCardAccent,
  type DetailIconBadgeProps,
  type DetailIconTone,
} from '@/components/patterns/detail-page'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { AppLinkProps } from '@/hooks/usePathHelper'
import { Link } from '@tanstack/react-router'
import { useCopyToClipboard } from '@uidotdev/usehooks'
import { Copy, Mail, Phone, UserRound } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { toast } from 'sonner'

type CopyValue = string | null | undefined

function isCopyableValue(value: CopyValue): value is string {
  return String(value ?? '').trim() !== ''
}

type DetailContactCopyButtonProps = {
  label: string
  successMessage: string
  value: CopyValue
}

function DetailContactCopyButton({ label, successMessage, value }: DetailContactCopyButtonProps) {
  const [, copyToClipboard] = useCopyToClipboard()

  if (!isCopyableValue(value)) {
    return null
  }

  return (
    <IconActionButton
      icon={Copy}
      label={label}
      variant="ghost"
      className="shrink-0 rounded-xl"
      aria-label={label}
      onClick={async () => {
        await copyToClipboard(value)
        toast.success(successMessage)
      }}
    />
  )
}

type DetailContactRowProps = {
  icon: ComponentType<{ className?: string }>
  iconTone: DetailIconTone
  label: string
  value: ReactNode
  copyValue: CopyValue
  copyLabel: string
  copySuccessMessage: string
}

function DetailContactRow({
  icon: Icon,
  iconTone,
  label,
  value,
  copyValue,
  copyLabel,
  copySuccessMessage,
}: DetailContactRowProps) {
  return (
    <div
      className={cn('flex items-center gap-3', getDetailWellClass({ compact: true, tone: 'mint' }))}
    >
      <Icon className={cn(detailPaleIconClass, detailIconBadgeToneClasses[iconTone])} />
      <div className="min-w-0 flex-1">
        <span className="sr-only">{label}</span>
        <p className="truncate text-base/6">{value || '(None provided)'}</p>
      </div>
      <DetailContactCopyButton
        label={copyLabel}
        successMessage={copySuccessMessage}
        value={copyValue}
      />
    </div>
  )
}

export type DetailContactCardProps = {
  title: string
  name?: ReactNode
  fallbackName?: ReactNode
  nameLinkProps?: AppLinkProps | null
  phone?: ReactNode
  phoneCopyValue?: CopyValue
  email?: ReactNode
  emailCopyValue?: CopyValue
  icon?: DetailIconBadgeProps['icon']
  iconTone?: DetailIconTone
  accent?: DetailCardAccent
  action?: ReactNode
  className?: string
}

export function DetailContactCard({
  title,
  name,
  fallbackName,
  nameLinkProps,
  phone,
  phoneCopyValue,
  email,
  emailCopyValue,
  icon = UserRound,
  iconTone = 'brand',
  accent = 'purchaser',
  action,
  className,
}: DetailContactCardProps) {
  const normalizedTitle = title.toLowerCase()
  const displayName = name || fallbackName || `No ${normalizedTitle} name provided`

  return (
    <DetailCard accent={accent} className={className} contentClassName="space-y-3 p-5">
      <div className="flex items-start gap-3">
        <DetailIconBadge icon={icon} tone={iconTone} />
        <div className="min-w-0 flex-1">
          <Typography variant="eyebrow">{title}</Typography>
          <div className="mt-2">
            {nameLinkProps ? (
              <Link {...nameLinkProps}>
                <Typography variant="cardTitle" weight="semibold" interaction="hoverUnderline">
                  {displayName}
                </Typography>
              </Link>
            ) : (
              <Typography variant="h5" className="pb-0">
                {displayName}
              </Typography>
            )}
          </div>
        </div>
        {action}
      </div>

      <div className="space-y-2.5">
        <DetailContactRow
          icon={Phone}
          iconTone={iconTone}
          label={`${title} phone`}
          value={phone}
          copyValue={phoneCopyValue}
          copyLabel={`Copy ${normalizedTitle} phone`}
          copySuccessMessage="Phone number copied to clipboard"
        />
        <DetailContactRow
          icon={Mail}
          iconTone={iconTone}
          label={`${title} email`}
          value={email}
          copyValue={emailCopyValue}
          copyLabel={`Copy ${normalizedTitle} email`}
          copySuccessMessage="Email copied to clipboard"
        />
      </div>
    </DetailCard>
  )
}
